import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TestimonialCards = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'CTO',
      company: 'TechFlow Solutions',
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
      rating: 5,
      date: '2023-11-15',
      category: 'client',
      testimonial: `Lwando Ntlemeza transformed our entire digital infrastructure with a vision that went far beyond our initial requirements. The cyberpunk-inspired interface they created wasn't just visually stunning—it revolutionized how our users interact with our platform.\n\nTheir ability to blend cutting-edge technology with intuitive design is unmatched. What impressed me most was their proactive approach to solving problems we didn't even know we had.`,
      project: 'Enterprise Dashboard Redesign',
      technologies: ['React', 'Node.js', 'AWS', 'WebGL'],
      impact: '40% increase in user engagement',
      color: '#00d4ff'
    },
    {
      id: 2,
      name: 'Marcus Rodriguez',
      role: 'Senior Developer',
      company: 'InnovateLab',
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
      rating: 5,
      date: '2023-10-28',
      category: 'mentee',
      testimonial: `Working with Lwando Ntlemeza as my mentor completely changed my career trajectory. Their teaching style is incredible—they don't just show you how to code, they teach you how to think like a digital architect.\n\nThe cyberpunk aesthetic isn't just visual flair; it represents a philosophy of pushing boundaries and reimagining what's possible. Under Lwando Ntlemeza's guidance, I've grown from a junior developer to leading my own team.`,
      project: 'Career Mentorship Program',
      technologies: ['Full-Stack Development', 'System Design', 'Leadership'],
      impact: 'Promoted to Senior Developer',
      color: '#00ff88'
    },
    {
      id: 3,
      name: 'Dr. Emily Watson',
      role: 'Product Manager',
      company: 'FutureTech Industries',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      rating: 5,
      date: '2023-12-02',
      category: 'client',
      testimonial: `Lwando Ntlemeza doesn't just build applications; they craft digital experiences that feel like glimpses into the future. Our collaboration resulted in a product that our competitors are still trying to understand.\n\nTheir technical expertise is matched only by their ability to translate complex concepts into solutions that drive real business value. The attention to detail and innovative approach exceeded all expectations.`,
      project: 'AI-Powered Analytics Platform',
      technologies: ['Python', 'Machine Learning', 'React', 'Docker'],
      impact: '60% reduction in analysis time',
      color: '#8844ff'
    },
    {
      id: 4,
      name: 'James Park',
      role: 'Startup Founder',
      company: 'NexGen Ventures',
      avatar: 'https://randomuser.me/api/portraits/men/35.jpg',
      rating: 5,
      date: '2023-09-20',
      category: 'client',
      testimonial: `As a non-technical founder, I was intimidated by the development process. Lwando Ntlemeza changed that completely. They explained everything in terms I could understand while delivering a product that looked like it came from the future.\n\nThe cyberpunk aesthetic perfectly captured our brand vision of being at the forefront of innovation. Lwando Ntlemeza didn't just meet our requirements—they elevated our entire concept.`,
      project: 'Startup MVP Development',
      technologies: ['Next.js', 'TypeScript', 'PostgreSQL', 'Vercel'],
      impact: 'Successful Series A funding',
      color: '#ffaa00'
    },
    {
      id: 5,
      name: 'Lisa Thompson',
      role: 'Junior Developer',
      company: 'CodeCraft Academy',
      avatar: 'https://randomuser.me/api/portraits/women/24.jpg',
      rating: 5,
      date: '2023-11-08',
      category: 'mentee',
      testimonial: `Lwando Ntlemeza's mentorship program is unlike anything else in the industry. The combination of technical depth and philosophical approach to development opened my eyes to what programming could be.\n\nThey taught me that code is art, and every project is an opportunity to create something beautiful and meaningful. The cyberpunk aesthetic reflects this perfectly—it's about transcending limitations and creating the impossible.`,
      project: 'Junior to Mid-Level Transition',
      technologies: ['React', 'Node.js', 'Database Design', 'System Architecture'],
      impact: '150% salary increase',
      color: '#ff6b35'
    },
    {
      id: 6,
      name: 'David Kim',
      role: 'Engineering Manager',
      company: 'CloudScale Systems',
      avatar: 'https://randomuser.me/api/portraits/men/42.jpg',
      rating: 5,
      date: '2023-10-15',
      category: 'peer',
      testimonial: `Collaborating with Lwando Ntlemeza on our microservices architecture was a masterclass in modern development. Their approach to problem-solving is both methodical and creative.\n\nWhat sets Lwando Ntlemeza apart is their ability to see the bigger picture while maintaining attention to the smallest details. The cyberpunk-inspired monitoring dashboard they created is both functional and a work of art.`,
      project: 'Microservices Architecture',
      technologies: ['Kubernetes', 'Docker', 'Go', 'Monitoring'],
      impact: '99.9% system uptime',
      color: '#00d4ff'
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const handleTestimonialChange = (index) => {
    setActiveTestimonial(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'client': return 'Briefcase';
      case 'mentee': return 'GraduationCap';
      case 'peer': return 'Users';
      default: return 'MessageSquare';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'client': return 'Client Project';
      case 'mentee': return 'Mentorship';
      case 'peer': return 'Collaboration';
      default: return 'Testimonial';
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Holographic Testimonials
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          Floating testimonials from clients and mentees with subtle parallax effects and social proof validation
        </p>
      </motion.div>

      <div className="relative">
        {/* Main Testimonial Display */}
        <div className="relative min-h-[380px] md:min-h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTestimonial}
              className="glass-morphism rounded-2xl p-6 md:p-8 cyber-border-active cyber-glow"
              initial={{ opacity: 0, scale: 0.95, rotateY: -10 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 0.95, rotateY: 10 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {(() => {
                const testimonial = testimonials[activeTestimonial];
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Testimonial Content */}
                    <div className="lg:col-span-2">
                      <div className="flex items-center space-x-3 mb-6">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${testimonial.color}20` }}
                        >
                          <Icon 
                            name={getCategoryIcon(testimonial.category)} 
                            size={16} 
                            style={{ color: testimonial.color }} 
                          />
                        </div>
                        <span 
                          className="font-mono text-sm font-medium"
                          style={{ color: testimonial.color }}
                        >
                          {getCategoryLabel(testimonial.category)}
                        </span>
                        <div className="flex space-x-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Icon 
                              key={i} 
                              name="Star" 
                              size={14} 
                              className="text-brand-orange fill-current" 
                            />
                          ))}
                        </div>
                      </div>

                      <blockquote className="text-text-secondary leading-relaxed font-inter text-base mb-6">
                        {testimonial.testimonial.split('\n\n').map((paragraph, idx) => (
                          <motion.p
                            key={idx}
                            className="mb-4 last:mb-0"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }}
                          >
                            "{paragraph}"
                          </motion.p>
                        ))}
                      </blockquote>

                      {/* Project Details */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-inter font-semibold text-foreground mb-2">
                            Project: {testimonial.project}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {testimonial.technologies.map((tech, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 rounded-lg text-xs font-mono border"
                                style={{
                                  backgroundColor: `${testimonial.color}10`,
                                  borderColor: `${testimonial.color}40`,
                                  color: testimonial.color
                                }}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Icon name="TrendingUp" size={16} className="text-brand-green" />
                            <span className="text-text-secondary">Impact:</span>
                            <span className="text-brand-green font-medium">
                              {testimonial.impact}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Author Information */}
                    <div className="lg:col-span-1">
                      <div className="glass-morphism rounded-xl p-4 md:p-6 cyber-border h-full">
                        <div className="text-center">
                          <div className="relative mb-4">
                            <div
                              className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-full flex items-center justify-center cyber-glow border-2"
                              style={{ backgroundColor: `${testimonial.color}20`, borderColor: testimonial.color }}
                            >
                              <Icon name={getCategoryIcon(testimonial.category)} size={32} style={{ color: testimonial.color }} />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-brand-green rounded-full border-2 border-background flex items-center justify-center">
                              <Icon name="CheckCircle" size={12} className="text-background" />
                            </div>
                          </div>

                          <h3 className="font-inter font-bold text-foreground text-lg mb-1">
                            {testimonial.name}
                          </h3>
                          <p className="text-text-secondary text-sm mb-1">
                            {testimonial.role}
                          </p>
                          <p 
                            className="font-mono text-xs font-medium mb-4"
                            style={{ color: testimonial.color }}
                          >
                            {testimonial.company}
                          </p>

                          <div className="space-y-3 text-xs text-text-tertiary">
                            <div className="flex items-center justify-center space-x-2">
                              <Icon name="Calendar" size={12} />
                              <span>{formatDate(testimonial.date)}</span>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                              <Icon name="Shield" size={12} />
                              <span>Verified Client</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Testimonial Navigation */}
        <div className="flex justify-center mt-6 space-x-2 flex-wrap">
          {testimonials.map((testimonial, index) => (
            <motion.button
              key={testimonial.id}
              className={`w-12 h-12 rounded-lg border-2 transition-all duration-300 flex items-center justify-center ${
                activeTestimonial === index
                  ? 'cyber-border-active cyber-glow scale-110' :'cyber-border hover:cyber-glow'
              }`}
              style={{
                backgroundColor: activeTestimonial === index ? `${testimonial.color}20` : 'transparent',
                borderColor: testimonial.color
              }}
              onClick={() => handleTestimonialChange(index)}
              whileHover={{ scale: activeTestimonial === index ? 1.1 : 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Icon name={getCategoryIcon(testimonial.category)} size={20} style={{ color: testimonial.color }} />
            </motion.button>
          ))}
        </div>

        {/* Auto-play Controls */}
        <div className="flex justify-center mt-4">
          <motion.button
            className="flex items-center space-x-2 px-4 py-2 rounded-lg cyber-border hover:cyber-glow transition-all duration-300"
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon 
              name={isAutoPlaying ? "Pause" : "Play"} 
              size={16} 
              className="text-text-secondary" 
            />
            <span className="text-sm text-text-secondary font-mono">
              {isAutoPlaying ? 'Pause' : 'Play'} Auto-rotation
            </span>
          </motion.button>
        </div>
      </div>

      {/* Testimonial Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="w-12 h-12 bg-brand-cyan/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="Star" size={24} className="text-brand-cyan" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            5.0
          </div>
          <div className="text-sm text-text-secondary">
            Average Rating
          </div>
        </motion.div>

        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="w-12 h-12 bg-brand-green/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="Users" size={24} className="text-brand-green" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            {testimonials.length}
          </div>
          <div className="text-sm text-text-secondary">
            Happy Clients
          </div>
        </motion.div>

        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="w-12 h-12 bg-brand-purple/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="Briefcase" size={24} className="text-brand-purple" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            {testimonials.filter(t => t.category === 'client').length}
          </div>
          <div className="text-sm text-text-secondary">
            Client Projects
          </div>
        </motion.div>

        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="w-12 h-12 bg-brand-orange/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="GraduationCap" size={24} className="text-brand-orange" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            {testimonials.filter(t => t.category === 'mentee').length}
          </div>
          <div className="text-sm text-text-secondary">
            Mentees Guided
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TestimonialCards;